import { useState, useEffect, useRef } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { Copy, Check, BookOpen, Terminal, Key, ArrowRight, Plug, ExternalLink, ChevronDown, ChevronRight, Download, Car, User, Loader2 } from 'lucide-react'

const VITE_URL = import.meta.env.VITE_APP_URL || 'all.acsm.org.br'
// Remove protocolo e barra final se existirem
const API_HOST = VITE_URL.replace(/^https?:\/\//, '').replace(/\/$/, '')

export function ApiDocsPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    useEffect(() => { setPageTitle('Documentação da API') }, [setPageTitle])

    const docRef = useRef<HTMLDivElement>(null)
    const [generating, setGenerating] = useState(false)
    const [copied, setCopied] = useState<string | null>(null)
    const [expandedSection, setExpandedSection] = useState<string | null>('request')
    const [exampleType, setExampleType] = useState<'veiculo' | 'cpf'>('veiculo')

    const copyBlock = (text: string, id: string) => {
        navigator.clipboard.writeText(text)
        setCopied(id)
        setTimeout(() => setCopied(null), 2000)
    }

    const toggleSection = (id: string) => {
        setExpandedSection(expandedSection === id ? null : id)
    }

    const requestVeiculo = `{
  "codigo_servico": "PROT-VEICULAR-01",
  "dia_vencimento": 10,
  "contratante": {
    "nome": "João da Silva",
    "cpf": "123.456.789-00",
    "email": "joao@email.com",
    "telefone": "(11) 99999-9999",
    "endereco": {
      "rua": "Rua Exemplo", "numero": "123", "bairro": "Centro",
      "cidade": "São Paulo", "uf": "SP", "cep": "01001-000"
    }
  },
  "veiculo": {
    "placa": "ABC1D23",
    "marca": "Fiat",
    "modelo": "Uno",
    "ano": 2022,
    "cor": "Prata"
  }
}`

    const requestCpfOnly = `{
  "codigo_servico": "ASSIST-RESIDENCIAL",
  "dia_vencimento": 10,
  "contratante": {
    "nome": "Maria de Souza",
    "cpf": "987.654.321-11",
    "email": "maria@email.com",
    "telefone": "(21) 98888-7777",
    "endereco": {
      "rua": "Av Paulista", "numero": "1000", "bairro": "Bela Vista",
      "cidade": "São Paulo", "uf": "SP", "cep": "01310-100"
    }
  }
}`

    const currentRequest = exampleType === 'veiculo' ? requestVeiculo : requestCpfOnly

    const exampleResponse = `{
  "ok": true,
  "contrato": {
    "id": "8125aa5e-224b-464a-be4f-d544c0d3cdfa",
    "status": "pendente_adesao",
    "data_inicio": "2026-04-07",
    "valor_mensal": 49.9,
    "ciclo_faturamento": "mensal",
    "dia_vencimento": 10,
    "data_primeira_mensalidade": "2026-05-10"
  },
  "associado": {
    "id": "f389c485-3aac-4d6d-bb4a-84aae7f37c28",
    "nome": "FABIO RIGOLI DA ROSA",
    "cpf": "00814026940"
  },
  "veiculo": ${exampleType === 'veiculo' ? '{ "id": "uuid", "placa": "ABC1D23" }' : 'null'},
  "adesao": {
    "valor": 49.90,
    "url_pagamento": "https://www.asaas.com/i/xxx"
  }
}`

    const exampleStatusResponse = `{
  "ok": true,
  "contrato": {
    "id": "8125aa5e-224b-464a-be4f-d544c0d3cdfa",
    "status": "ativo",
    "data_inicio": "2026-04-07",
    "valor_mensal": 49.9,
    "dia_vencimento": 10,
    "associado": {
      "id": "f389c485-3aac-4d6d-bb4a-84aae7f37c28",
      "nome": "FABIO RIGOLI DA ROSA",
      "cpf": "00814026940",
      "email": "fabio@rigolidarosa.com"
    },
    "veiculo": {
      "id": "uuid",
      "placa": "ABC1D23",
      "marca": "TOYOTA",
      "modelo": "COROLLA",
      "ano_modelo": 2024
    },
    "faturas": [
      {
        "id": "uuid",
        "valor": 49.9,
        "data_vencimento": "2026-05-10",
        "data_pagamento": "2026-05-09",
        "status": "paga",
        "tipo": "mensalidade"
      }
    ]
  }
}`

    const prodUrl = `${API_HOST}/api-contratar`
    const homologUrl = `${API_HOST}/api-contratar-homologacao`
    const statusUrl = `${API_HOST}/api-status`

    const curlExample = `curl -X POST https://${prodUrl} \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: SUA_API_KEY" \\
  -d '${currentRequest}'`

    const curlStatusExample = `curl -X GET "https://${statusUrl}?id=CHAVE_DO_CONTRATO" \\
  -H "x-api-key: SUA_API_KEY"`

    const handlePrint = async () => {
        setGenerating(true)
        
        // Injeta o script do html2pdf via CDN se não existir
        if (!(window as any).html2pdf) {
            const script = document.createElement('script')
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js'
            document.head.appendChild(script)
            await new Promise((resolve) => {
                script.onload = resolve
            })
        }

        const html2pdf = (window as any).html2pdf

        try {
            const exportHtml = `
                <div style="font-family: Arial, sans-serif; color: #1e293b; padding: 25px; line-height: 1.4; font-size: 11px;">
                    <div style="border-bottom: 3px solid #2563eb; padding-bottom: 10px; margin-bottom: 20px;">
                        <h1 style="color: #2563eb; margin: 0; font-size: 22px;">Manual de Integração API</h1>
                        <p style="color: #64748b; margin: 5px 0 0 0; font-size: 12px;">Versão 1.1 - ACSM Multiveicular</p>
                    </div>

                    <h2 style="font-size: 16px; color: #0f172a; border-left: 4px solid #f59e0b; padding-left: 10px; margin-top: 20px; background: #fffbeb;">1. Ambientes</h2>
                    <table style="width: 100%; border-collapse: collapse; margin-top: 5px;">
                        <tr style="background: #f8fafc; text-align: left;">
                            <th style="border: 1px solid #e2e8f0; padding: 8px;">Ambiente</th>
                            <th style="border: 1px solid #e2e8f0; padding: 8px;">Endpoint URL</th>
                        </tr>
                        <tr>
                            <td style="border: 1px solid #e2e8f0; padding: 8px; font-weight: bold; color: #10b981;">Produção</td>
                            <td style="border: 1px solid #e2e8f0; padding: 8px; font-family: monospace;">https://${prodUrl}</td>
                        </tr>
                        <tr>
                            <td style="border: 1px solid #e2e8f0; padding: 8px; font-weight: bold; color: #f59e0b;">Homologação (Sandbox)</td>
                            <td style="border: 1px solid #e2e8f0; padding: 8px; font-family: monospace;">https://${homologUrl}</td>
                        </tr>
                    </table>

                    <h2 style="font-size: 16px; color: #0f172a; border-left: 4px solid #2563eb; padding-left: 10px; margin-top: 25px; background: #eff6ff;">2. Tabelas de Campos (Request Body)</h2>
                    
                    <h3 style="font-size: 13px; color: #2563eb; margin-top: 15px;">2.1 Campos Raiz</h3>
                    <table style="width: 100%; border-collapse: collapse; margin-top: 5px;">
                        <tr style="background: #f8fafc; text-align: left; font-size: 10px;">
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Campo</th>
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Tipo</th>
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Obrig.</th>
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Descrição</th>
                        </tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">codigo_servico</td><td style="border: 1px solid #e2e8f0; padding: 6px;">string</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Sim</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Código amigável ou ID do serviço</td></tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">dia_vencimento</td><td style="border: 1px solid #e2e8f0; padding: 6px;">number</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Sim</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Dia do vencimento mensal (1-28)</td></tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">contratante</td><td style="border: 1px solid #e2e8f0; padding: 6px;">object</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Sim</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Objeto com dados do contratante</td></tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">veiculo</td><td style="border: 1px solid #e2e8f0; padding: 6px;">object</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Não*</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Obrigatório se serviço for veicular</td></tr>
                    </table>

                    <h3 style="font-size: 13px; color: #2563eb; margin-top: 15px;">2.2 Objeto Contratante</h3>
                    <table style="width: 100%; border-collapse: collapse; margin-top: 5px;">
                        <tr style="background: #f8fafc; text-align: left; font-size: 10px;">
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Campo</th>
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Tipo</th>
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Obrig.</th>
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Descrição</th>
                        </tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">nome</td><td style="border: 1px solid #e2e8f0; padding: 6px;">string</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Sim</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Nome completo</td></tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">cpf</td><td style="border: 1px solid #e2e8f0; padding: 6px;">string</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Sim</td><td style="border: 1px solid #e2e8f0; padding: 6px;">CPF (com ou sem formatação)</td></tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">email</td><td style="border: 1px solid #e2e8f0; padding: 6px;">string</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Sim</td><td style="border: 1px solid #e2e8f0; padding: 6px;">E-mail de contato</td></tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">telefone</td><td style="border: 1px solid #e2e8f0; padding: 6px;">string</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Sim</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Telefone com DDD</td></tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">endereco</td><td style="border: 1px solid #e2e8f0; padding: 6px;">object</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Sim</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Dados de endereço</td></tr>
                    </table>

                    <div style="page-break-before: always;"></div>

                    <h3 style="font-size: 13px; color: #2563eb; margin-top: 15px;">2.3 Objeto Veículo</h3>
                    <table style="width: 100%; border-collapse: collapse; margin-top: 5px;">
                        <tr style="background: #f8fafc; text-align: left; font-size: 10px;">
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Campo</th>
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Tipo</th>
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Obrig.</th>
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Descrição</th>
                        </tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">placa</td><td style="border: 1px solid #e2e8f0; padding: 6px;">string</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Sim</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Placa do veículo</td></tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">marca</td><td style="border: 1px solid #e2e8f0; padding: 6px;">string</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Sim</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Ex: Fiat, Chevrolet</td></tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">modelo</td><td style="border: 1px solid #e2e8f0; padding: 6px;">string</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Sim</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Ex: Uno, Onix</td></tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px;">ano</td><td style="border: 1px solid #e2e8f0; padding: 6px;">number</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Não</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Ano de fabricação</td></tr>
                    </table>

                    <h2 style="font-size: 16px; color: #0f172a; border-left: 4px solid #10b981; padding-left: 10px; margin-top: 30px; background: #ecfdf5;">3. Exemplos de Requisição</h2>
                    
                    <h3 style="font-size: 12px; margin-top: 10px;">3.1 Serviço Veicular</h3>
                    <pre style="background: #0f172a; color: #e2e8f0; padding: 10px; border-radius: 6px; font-size: 10px; white-space: pre-wrap;">
{
  "codigo_servico": "SEGURO-CNH",
  "dia_vencimento": 10,
  "contratante": {
    "nome": "João da Silva",
    "cpf": "123.456.789-00",
    "email": "joao@email.com",
    "telefone": "(11) 99999-9999",
    "endereco": { "rua": "Rua Exemplo", "numero": "123", "bairro": "Centro", "cidade": "São Paulo", "uf": "SP", "cep": "01001-000" }
  },
  "veiculo": { "placa": "ABC1D23", "marca": "Fiat", "modelo": "Uno", "ano": 2022, "cor": "Prata" }
}
                    </pre>

                    <h3 style="font-size: 12px; margin-top: 20px;">3.2 Serviço Somente CPF</h3>
                    <pre style="background: #0f172a; color: #e2e8f0; padding: 10px; border-radius: 6px; font-size: 10px; white-space: pre-wrap;">
{
  "codigo_servico": "ASSIST-RESIDENCIAL",
  "dia_vencimento": 10,
  "contratante": {
    "nome": "Maria de Souza",
    "cpf": "987.654.321-11",
    "email": "maria@email.com",
    "telefone": "(21) 98888-7777",
    "endereco": { "rua": "Av Paulista", "numero": "1000", "bairro": "Bela Vista", "cidade": "São Paulo", "uf": "SP", "cep": "01310-100" }
  }
}
                    </pre>

                    <div style="page-break-before: always;"></div>

                    <h2 style="font-size: 16px; color: #0f172a; border-left: 4px solid #6366f1; padding-left: 10px; margin-top: 30px; background: #f5f3ff;">4. Consulta e Retornos</h2>
                    
                    <h3 style="font-size: 12px;">4.1 Consulta de Status (GET)</h3>
                    <p>Endpoint: <code style="font-family: monospace; background: #f1f5f9; padding: 2px 4px;">/api-status?id={{id_contrato}}</code></p>
                    
                    <h3 style="font-size: 12px; margin-top: 20px;">4.2 Tabela de Erros</h3>
                    <table style="width: 100%; border-collapse: collapse; margin-top: 5px; font-size: 10px;">
                        <tr style="background: #f8fafc; text-align: left;">
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Status</th>
                            <th style="border: 1px solid #e2e8f0; padding: 6px;">Descrição</th>
                        </tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px; font-weight: bold;">401 Unauthorized</td><td style="border: 1px solid #e2e8f0; padding: 6px;">API Key inválida ou não informada.</td></tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px; font-weight: bold;">400 Bad Request</td><td style="border: 1px solid #e2e8f0; padding: 6px;">Erro de validação nos campos informados.</td></tr>
                        <tr><td style="border: 1px solid #e2e8f0; padding: 6px; font-weight: bold;">404 Not Found</td><td style="border: 1px solid #e2e8f0; padding: 6px;">ID de contrato não localizado.</td></tr>
                    </table>

                    <div style="margin-top: 40px; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 15px; font-size: 10px; color: #94a3b8;">
                        Documento Oficial - ACSM Multiveicular - ${new Date().toLocaleDateString('pt-BR')}
                    </div>
                </div>
            `

            const opt = {
                margin:       10,
                filename:     'Manual_API_ACSM_Multiveicular.pdf',
                image:        { type: 'jpeg', quality: 0.98 },
                html2canvas:  { scale: 2 },
                jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
            }

            await html2pdf().set(opt).from(exportHtml).save()
        } catch (error) {
            console.error('PDF Error:', error)
            alert('Erro ao gerar PDF. Ocorreu um problema na formatação do documento.')
        } finally {
            setGenerating(false)
        }
    }

    return (
        <div className="space-y-6 animate-fade-in max-w-4xl pb-20" ref={docRef}>
            {/* Context/Intro */}
            <div className="glass-card p-6 bg-primary-600 border-none text-white relative overflow-hidden print:bg-transparent print:text-surface-900 print:shadow-none print:p-0 print:m-0" id="api-doc-header">
                <style dangerouslySetInnerHTML={{ __html: `
                    @media print {
                        /* Esconde ABSOLUTAMENTE tudo que não seja a doc */
                        nav, aside, header, footer, button, .sidebar, .top-nav, .print\\:hidden, [role="complementary"] { 
                            display: none !important; 
                        }
                        
                        /* Reseta o esqueleto do site para ocupar a tela toda */
                        html, body, #root, main, div.flex, .container { 
                            margin: 0 !important; 
                            padding: 0 !important; 
                            width: 100% !important; 
                            max-width: 100% !important;
                            min-width: 100% !important;
                            display: block !important;
                            overflow: visible !important;
                        }

                        /* Garante que a nossa página de doc use o espaço todo */
                        .animate-fade-in { 
                            max-width: 100% !important; 
                            padding: 20px !important;
                            margin: 0 !important;
                        }

                        .glass-card { 
                            border: 1px solid #e2e8f0 !important; 
                            box-shadow: none !important; 
                            background: white !important; 
                            padding: 20px !important;
                            margin-bottom: 20px !important;
                            break-inside: avoid;
                        }

                        #api-doc-header {
                            background: #2563eb !important; /* Azul escuro no PDF */
                            color: white !important;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }

                        pre { 
                            white-space: pre-wrap !important; 
                            word-break: break-all !important; 
                            background: #f8fafc !important; 
                            color: #1e293b !important; 
                            font-size: 10px !important;
                        }
                        
                        h1, h2, h3 { color: #000 !important; }
                    }
                `}} />
                
                <div className="relative z-10 flex items-start gap-4">
                    <div className="p-3 bg-white/20 rounded-xl print:hidden">
                        <Terminal size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Documentação API Parceiro v1.1</h1>
                        <p className="text-white/80 mt-1 print:text-surface-700">Instruções técnicas para integração com o nosso sistema.</p>
                        
                        <div className="flex flex-wrap items-center gap-3 mt-4 print:hidden">
                            <button 
                                onClick={handlePrint} 
                                disabled={generating}
                                className="flex items-center gap-1.5 px-3 py-1 bg-white/15 hover:bg-white/25 text-white rounded-lg text-xs font-semibold transition-all shadow-sm active:scale-95 disabled:opacity-50"
                            >
                                {generating ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                                {generating ? 'Gerando PDF...' : 'Baixar PDF'}
                            </button>
                            
                            <a href="/docs/contratacao_api.postman_collection.json" download
                                className="flex items-center gap-1.5 px-3 py-1 bg-orange-600 hover:bg-orange-500 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm">
                                <ExternalLink size={14} /> Postman
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Environments Table */}
            <div className="glass-card p-6 border-l-4 border-l-amber-500">
                <div className="flex items-center gap-2 mb-4">
                    <BookOpen size={18} className="text-amber-600" />
                    <h3 className="font-bold text-surface-900">Ambientes Disponíveis</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-surface-50">
                            <tr className="text-left text-xs text-surface-500">
                                <th className="p-3 font-semibold rounded-tl-xl font-mono uppercase">Ambiente</th>
                                <th className="p-3 font-semibold rounded-tr-xl font-mono uppercase">Endpoint Base</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-surface-100 border-x border-b border-surface-100 rounded-b-xl overflow-hidden">
                            <tr>
                                <td className="p-3 font-bold text-emerald-700 bg-emerald-50/30">Produção</td>
                                <td className="p-3 font-mono text-xs text-surface-800">https://{prodUrl}</td>
                            </tr>
                            <tr>
                                <td className="p-3 font-bold text-amber-700 bg-amber-50/30">Homologação</td>
                                <td className="p-3 font-mono text-xs text-surface-800">https://{homologUrl}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p className="text-xs text-surface-500 mt-3 italic">
                    * O ambiente de Homologação utiliza o <strong>Asaas Sandbox</strong>. Nenhuma cobrança real é gerada neste modo.
                </p>
            </div>


            {/* Partner Webhook Info */}
            <div className="glass-card">
                <div className="p-4 border-b border-surface-100 bg-surface-50/50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-success-50 text-success-600 rounded-lg">
                            <Plug size={16} />
                        </div>
                        <div>
                            <h3 className="font-bold text-surface-900">Webhook de Retorno (Para o Parceiro)</h3>
                            <p className="text-xs text-surface-500">Notificação automática de ativação</p>
                        </div>
                    </div>
                </div>
                <div className="p-5">
                    <p className="text-sm text-surface-700 mb-3">
                        Se você configurar uma <strong>URL de Webhook</strong> no seu cadastro de parceiro, nosso sistema enviará um POST sempre que um contrato for ativado (pagamento da adesão compensado).
                    </p>
                    <div className="bg-surface-950 rounded-lg p-4 font-mono text-xs text-emerald-400">
                        {`// Exemplo de payload enviado para seu webhook\n{\n  "evento": "CONTRATO_ATIVADO",\n  "contrato_id": "uuid",\n  "status": "ativo",\n  "data_ativacao": "2026-04-07T..."\n}`}
                    </div>
                </div>
            </div>

            {/* Auth Session */}
            <div className="glass-card p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Key size={18} className="text-amber-500" />
                    <h3 className="font-semibold text-surface-900">Autenticação</h3>
                </div>
                <p className="text-sm text-surface-700/80 mb-3">
                    Todas as requisições devem incluir o header <code className="px-1.5 py-0.5 rounded bg-surface-100 text-xs font-mono text-primary-700">x-api-key</code> com a chave de API do parceiro.
                </p>
                <div className="relative bg-surface-950 rounded-xl p-4 font-mono text-sm text-emerald-400">
                    <span className="text-surface-400">Header:</span> x-api-key: <span className="text-amber-400">pk_live_xxxxxxxxxx...</span>
                    <button onClick={() => copyBlock('x-api-key: SUA_API_KEY', 'auth')}
                        className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors">
                        {copied === 'auth' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                    </button>
                </div>
                <p className="text-xs text-surface-500 mt-2">
                    A API Key é gerada automaticamente no cadastro do parceiro (menu <strong>Parceiros</strong>).
                </p>
            </div>

            {/* Endpoint - Contratar */}
            <div className="glass-card p-6 border-l-4 border-l-emerald-500">
                <div className="flex items-center gap-3 mb-4">
                    <span className="px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-bold tracking-wide">POST</span>
                    <code className="text-sm font-mono text-surface-900 font-semibold">https://{prodUrl}</code>
                </div>
                <p className="text-sm text-surface-700/80 mb-4">
                    Cria um contrato para um contratante (novo ou existente), gerando automaticamente as cobranças no Asaas.
                </p>

                {/* Request Body */}
                <div className="border border-surface-100 rounded-xl overflow-hidden mb-4">
                    <button onClick={() => toggleSection('request')}
                        className="w-full flex items-center justify-between px-4 py-3 bg-surface-50 hover:bg-surface-100 transition-colors print:hidden">
                        <span className="text-sm font-semibold text-surface-900 flex items-center gap-2">
                            <ArrowRight size={14} className="text-blue-500" />
                            Body da Requisição
                        </span>
                        {(expandedSection === 'request' || expandedSection === 'all') ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {(expandedSection === 'request' || expandedSection === 'all') && (
                        <div className="p-4 space-y-4">
                            {/* Seletor de Tipo de Exemplo */}
                            <div className="flex gap-2">
                                <button 
                                    onClick={() => setExampleType('veiculo')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 transition-all
                                        ${exampleType === 'veiculo' 
                                            ? 'bg-primary-50 border-primary-200 text-primary-700 shadow-sm' 
                                            : 'bg-white border-surface-100 text-surface-400 hover:bg-surface-50'}`}
                                >
                                    <Car size={16} />
                                    <span className="text-xs font-semibold">Serviço com Veículo</span>
                                </button>
                                <button 
                                    onClick={() => setExampleType('cpf')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 transition-all
                                        ${exampleType === 'cpf' 
                                            ? 'bg-primary-50 border-primary-200 text-primary-700 shadow-sm' 
                                            : 'bg-white border-surface-100 text-surface-400 hover:bg-surface-50'}`}
                                >
                                    <User size={16} />
                                    <span className="text-xs font-semibold">Serviço Somente CPF</span>
                                </button>
                            </div>

                            {/* JSON Preview */}
                            <div className="relative group">
                                <pre className="bg-surface-950 rounded-xl p-4 text-xs font-mono text-blue-300 overflow-x-auto leading-relaxed whitespace-pre-wrap">
                                    {currentRequest}
                                </pre>
                                <button onClick={() => copyBlock(currentRequest, 'json-req')}
                                    className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/10 opacity-0 group-hover:opacity-100 hover:bg-white/20 text-white/60 hover:text-white transition-all">
                                    {copied === 'json-req' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                                </button>
                            </div>

                            {/* Campos obrigatórios */}
                            <div>
                                <p className="text-xs font-semibold text-surface-600 uppercase tracking-wide mb-2">Campos Raiz</p>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-xs text-surface-500 border-b border-surface-100">
                                                <th className="pb-2 pr-4 font-medium">Campo</th>
                                                <th className="pb-2 pr-4 font-medium">Tipo</th>
                                                <th className="pb-2 pr-4 font-medium">Obrig.</th>
                                                <th className="pb-2 font-medium">Descrição</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-surface-50">
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">codigo_servico</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">string</td>
                                                <td className="py-2 pr-4"><span className="text-emerald-600 text-xs font-bold">✓</span></td>
                                                <td className="py-2 text-xs text-surface-600">Código amigável ou ID do serviço</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">dia_vencimento</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">number (1-28)</td>
                                                <td className="py-2 pr-4"><span className="text-emerald-600 text-xs font-bold">✓</span></td>
                                                <td className="py-2 text-xs text-surface-600">Dia do vencimento mensal</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">contratante</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">object</td>
                                                <td className="py-2 pr-4"><span className="text-emerald-600 text-xs font-bold">✓</span></td>
                                                <td className="py-2 text-xs text-surface-600">Dados do contratante</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">veiculo</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">object</td>
                                                <td className="py-2 pr-4"><span className="text-amber-500 text-xs font-bold">⚠</span></td>
                                                <td className="py-2 text-xs text-surface-600">Obrigatório se serviço exige veículo</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Contratante */}
                            <div>
                                <p className="text-xs font-semibold text-surface-600 uppercase tracking-wide mb-2">Objeto <code className="text-primary-600">contratante</code></p>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-xs text-surface-500 border-b border-surface-100">
                                                <th className="pb-2 pr-4 font-medium">Campo</th>
                                                <th className="pb-2 pr-4 font-medium">Tipo</th>
                                                <th className="pb-2 pr-4 font-medium">Obrig.</th>
                                                <th className="pb-2 font-medium">Descrição</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-surface-50">
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">nome</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">string</td>
                                                <td className="py-2 pr-4"><span className="text-emerald-600 text-xs font-bold">✓</span></td>
                                                <td className="py-2 text-xs text-surface-600">Nome completo</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">cpf</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">string</td>
                                                <td className="py-2 pr-4"><span className="text-emerald-600 text-xs font-bold">✓</span></td>
                                                <td className="py-2 text-xs text-surface-600">CPF (com ou sem formatação)</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">email</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">string</td>
                                                <td className="py-2 pr-4"><span className="text-surface-300 text-xs">—</span></td>
                                                <td className="py-2 text-xs text-surface-600">E-mail de contato</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">telefone</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">string</td>
                                                <td className="py-2 pr-4"><span className="text-surface-300 text-xs">—</span></td>
                                                <td className="py-2 text-xs text-surface-600">Telefone de contato</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">endereco</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">object</td>
                                                <td className="py-2 pr-4"><span className="text-surface-300 text-xs">—</span></td>
                                                <td className="py-2 text-xs text-surface-600">rua, numero, bairro, cidade, uf, cep</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Veículo */}
                            <div>
                                <p className="text-xs font-semibold text-surface-600 uppercase tracking-wide mb-2">Objeto <code className="text-primary-600">veiculo</code></p>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-xs text-surface-500 border-b border-surface-100">
                                                <th className="pb-2 pr-4 font-medium">Campo</th>
                                                <th className="pb-2 pr-4 font-medium">Tipo</th>
                                                <th className="pb-2 pr-4 font-medium">Obrig.</th>
                                                <th className="pb-2 font-medium">Descrição</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-surface-50">
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">placa</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">string</td>
                                                <td className="py-2 pr-4"><span className="text-emerald-600 text-xs font-bold">✓</span></td>
                                                <td className="py-2 text-xs text-surface-600">Placa (ABC1234 ou ABC1D23)</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">marca</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">string</td>
                                                <td className="py-2 pr-4"><span className="text-emerald-600 text-xs font-bold">✓</span></td>
                                                <td className="py-2 text-xs text-surface-600">Ex: Fiat, Chevrolet</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">modelo</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">string</td>
                                                <td className="py-2 pr-4"><span className="text-emerald-600 text-xs font-bold">✓</span></td>
                                                <td className="py-2 text-xs text-surface-600">Ex: Uno, Onix</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">ano</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">number</td>
                                                <td className="py-2 pr-4"><span className="text-surface-300 text-xs">—</span></td>
                                                <td className="py-2 text-xs text-surface-600">Ano de fabricação</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">cor</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">string</td>
                                                <td className="py-2 pr-4"><span className="text-surface-300 text-xs">—</span></td>
                                                <td className="py-2 text-xs text-surface-600">Cor do veículo</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 pr-4 font-mono text-xs text-primary-700">chassi</td>
                                                <td className="py-2 pr-4 text-xs text-surface-500">string</td>
                                                <td className="py-2 pr-4"><span className="text-surface-300 text-xs">—</span></td>
                                                <td className="py-2 text-xs text-surface-600">Número do chassi</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Response */}
                <div className="border border-surface-100 rounded-xl overflow-hidden mb-4">
                    <button onClick={() => toggleSection('response')}
                        className="w-full flex items-center justify-between px-4 py-3 bg-surface-50 hover:bg-surface-100 transition-colors print:hidden">
                        <span className="text-sm font-semibold text-surface-900 flex items-center gap-2">
                            <ArrowRight size={14} className="text-emerald-500 rotate-180" />
                            Resposta (200 OK)
                        </span>
                        {(expandedSection === 'response' || expandedSection === 'all') ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {(expandedSection === 'response' || expandedSection === 'all') && (
                        <div className="p-4">
                            <div className="relative">
                                <pre className="bg-surface-950 rounded-xl p-4 text-xs font-mono text-emerald-400 overflow-x-auto leading-relaxed">{exampleResponse}</pre>
                                <button onClick={() => copyBlock(exampleResponse, 'response')}
                                    className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors">
                                    {copied === 'response' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                                </button>
                            </div>
                            <div className="mt-3 space-y-1.5">
                                <p className="text-xs text-surface-500">
                                    <code className="text-primary-600">adesao</code> — <code>null</code> se o serviço não tem taxa de adesão
                                </p>
                                <p className="text-xs text-surface-500">
                                    <code className="text-primary-600">veiculo</code> — <code>null</code> se o serviço não exige veículo
                                </p>
                                <p className="text-xs text-surface-500">
                                    <code className="text-primary-600">adesao.url_pagamento</code> — URL para o contratante pagar a adesão
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Errors */}
                <div className="border border-surface-100 rounded-xl overflow-hidden mb-4">
                    <button onClick={() => toggleSection('errors')}
                        className="w-full flex items-center justify-between px-4 py-3 bg-surface-50 hover:bg-surface-100 transition-colors print:hidden">
                        <span className="text-sm font-semibold text-surface-900 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-danger-500" />
                            Erros
                        </span>
                        {(expandedSection === 'errors' || expandedSection === 'all') ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {(expandedSection === 'errors' || expandedSection === 'all') && (
                        <div className="p-4">
                            <div className="space-y-3">
                                {[
                                    { code: 401, msg: 'API Key inválida ou parceiro não encontrado.', desc: 'API Key ausente ou incorreta' },
                                    { code: 403, msg: 'Parceiro inativo.', desc: 'Parceiro foi desativado' },
                                    { code: 400, msg: 'codigo_servico é obrigatório', desc: 'Campo obrigatório vazio' },
                                    { code: 400, msg: 'Este serviço exige dados do veículo', desc: 'Veículo não enviado para serviço veicular' },
                                    { code: 400, msg: 'dia_vencimento deve ser entre 1 e 28', desc: 'Dia fora do range' },
                                    { code: 400, msg: 'Serviço não encontrado ou inativo', desc: 'codigo_servico inválido' },
                                ].map((err, i) => (
                                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-surface-50">
                                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${err.code === 401 ? 'bg-amber-100 text-amber-700' : err.code === 403 ? 'bg-red-100 text-red-700' : 'bg-surface-200 text-surface-600'}`}>
                                            {err.code}
                                        </span>
                                        <div>
                                            <code className="text-xs font-mono text-surface-700">{err.msg}</code>
                                            <p className="text-xs text-surface-400 mt-0.5">{err.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* cURL */}
                <div className="border border-surface-100 rounded-xl overflow-hidden">
                    <button onClick={() => toggleSection('curl')}
                        className="w-full flex items-center justify-between px-4 py-3 bg-surface-50 hover:bg-surface-100 transition-colors print:hidden">
                        <span className="text-sm font-semibold text-surface-900 flex items-center gap-2">
                            <Terminal size={14} className="text-violet-500" />
                            Exemplo cURL
                        </span>
                        {(expandedSection === 'curl' || expandedSection === 'all') ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {(expandedSection === 'curl' || expandedSection === 'all') && (
                        <div className="p-4">
                            <div className="relative">
                                <pre className="bg-surface-950 rounded-xl p-4 text-xs font-mono text-sky-400 overflow-x-auto leading-relaxed whitespace-pre-wrap">{curlExample}</pre>
                                <button onClick={() => copyBlock(curlExample, 'curl')}
                                    className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors">
                                    {copied === 'curl' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Endpoint - Status */}
            <div className="glass-card p-6 border-l-4 border-l-primary-500">
                <div className="flex items-center gap-3 mb-4">
                    <span className="px-2.5 py-1 rounded-lg bg-primary-100 text-primary-700 text-xs font-bold tracking-wide">GET</span>
                    <code className="text-sm font-mono text-surface-900 font-semibold">https://{statusUrl}?id=...</code>
                </div>
                <p className="text-sm text-surface-700/80 mb-4">
                    Consulta a situação atual de um contrato específico, dados do associado, veículo e as últimas 5 faturas geradas.
                </p>

                {/* Response example */}
                <div className="border border-surface-100 rounded-xl overflow-hidden mb-4">
                    <div className="w-full flex items-center justify-between px-4 py-3 bg-surface-50 border-b border-surface-100">
                        <span className="text-sm font-semibold text-surface-900 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            Exemplo de Resposta (Status)
                        </span>
                    </div>
                    <div className="p-4">
                        <pre className="bg-surface-950 rounded-xl p-4 text-xs font-mono text-emerald-400 overflow-x-auto leading-relaxed">{exampleStatusResponse}</pre>
                    </div>
                </div>

                {/* cURL Status */}
                <div className="border border-surface-100 rounded-xl overflow-hidden">
                    <div className="w-full flex items-center justify-between px-4 py-3 bg-surface-50 border-b border-surface-100">
                        <span className="text-sm font-semibold text-surface-900 flex items-center gap-2">
                            <Terminal size={14} className="text-violet-500" />
                            Exemplo cURL (Status)
                        </span>
                    </div>
                    <div className="p-4">
                        <div className="relative">
                            <pre className="bg-surface-950 rounded-xl p-4 text-xs font-mono text-sky-400 overflow-x-auto leading-relaxed whitespace-pre-wrap">{curlStatusExample}</pre>
                            <button onClick={() => copyBlock(curlStatusExample, 'curl-status')}
                                className="absolute top-3 right-3 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors">
                                {copied === 'curl-status' ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Behavior Notes */}
            <div className="glass-card p-6">
                <h3 className="font-semibold text-surface-900 mb-4">Comportamento</h3>
                <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-50/50 border border-blue-100">
                        <span className="text-blue-500 mt-0.5 text-lg">📋</span>
                        <div>
                            <p className="text-sm font-medium text-surface-900">CPF existente é reutilizado</p>
                            <p className="text-xs text-surface-600 mt-0.5">Se o CPF já está cadastrado, o associado é reutilizado. Dados de contato são atualizados se enviados.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
                        <span className="text-emerald-500 mt-0.5 text-lg">💳</span>
                        <div>
                            <p className="text-sm font-medium text-surface-900">Cobranças automáticas</p>
                            <p className="text-xs text-surface-600 mt-0.5">A assinatura mensal é criada automaticamente no Asaas. As cobranças seguintes são geradas via webhook.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-orange-50/50 border border-orange-100">
                        <span className="text-orange-500 mt-0.5 text-lg">💰</span>
                        <div>
                            <p className="text-sm font-medium text-surface-900">Split automático</p>
                            <p className="text-xs text-surface-600 mt-0.5">O split de pagamento é configurado por serviço no painel admin. O integrador não precisa enviar dados de split.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-violet-50/50 border border-violet-100">
                        <span className="text-violet-500 mt-0.5 text-lg">🔑</span>
                        <div>
                            <p className="text-sm font-medium text-surface-900">API Key vinculada ao tenant</p>
                            <p className="text-xs text-surface-600 mt-0.5">Cada API Key identifica o parceiro e o tenant. O parceiro só acessa serviços do seu tenant.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Status Reference */}
            <div className="glass-card p-6">
                <h3 className="font-semibold text-surface-900 mb-4">Status do Contrato</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                        { status: 'pendente_adesao', desc: 'Aguardando pagamento da adesão', color: 'bg-amber-100 text-amber-700' },
                        { status: 'pendente_mensalidade', desc: 'Aguardando 1º pagamento da assinatura', color: 'bg-blue-100 text-blue-700' },
                        { status: 'ativo', desc: 'Contrato ativo, cobranças em dia', color: 'bg-emerald-100 text-emerald-700' },
                        { status: 'inadimplente', desc: 'Cobranças em atraso', color: 'bg-red-100 text-red-700' },
                        { status: 'suspenso', desc: 'Temporariamente suspenso', color: 'bg-surface-100 text-surface-600' },
                        { status: 'cancelado', desc: 'Contrato encerrado', color: 'bg-surface-100 text-surface-400' },
                    ].map(s => (
                        <div key={s.status} className="flex items-center gap-3 p-2.5 rounded-lg border border-surface-100">
                            <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${s.color}`}>{s.status}</span>
                            <span className="text-xs text-surface-600">{s.desc}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
